/**
 * Shared linked-orders modal view state derived from data accumulator context.
 * @module expandableTableForOrderAuth/hooks/useLinkedOrdersModalView
 */
import { useDataAccumulatorForSingleOrder } from "../../../context/dataAccumulatorForSingleOrder";
import { useClubbedDataFromFilteredSiblingOrders } from "./useClubbedDataFromFilteredSiblingOrders";

/** Modal title when filtered sibling orders are available in context. */
export const LINKED_ORDERS_MODAL_TITLE = "Update Linked Orders";

/**
 * Returns the modal title, preferring the linked-orders label when siblings exist.
 *
 * @param fallbackTitle - Title used when no filtered siblings are in context
 * @param tableLength - Number of clubbed sibling rows to display
 * @returns Dialog header text
 */
export const getLinkedOrdersModalTitle = (
  fallbackTitle: string,
  tableLength: number,
): string => {
  if (tableLength > 0) {
    return LINKED_ORDERS_MODAL_TITLE;
  }

  return fallbackTitle;
};

/**
 * Reads filtered sibling orders already present in context and exposes table
 * props for confirmation modals. Does not recompute the sibling filter.
 *
 * @returns Clubbed table data, loading/error flags, persisted selection state,
 *   and checkbox lock controls for linked-order confirmation modals
 */
export const useLinkedOrdersModalView = () => {
  const {
    siblingOrdersLoading,
    siblingOrdersError,
    selectedSiblingOrderIds,
    setSelectedSiblingOrderIds,
    isSiblingOrderSelectionDisabled,
    setIsSiblingOrderSelectionDisabled,
  } = useDataAccumulatorForSingleOrder();
  const expandableTableData = useClubbedDataFromFilteredSiblingOrders();
  const hasLinkedOrders = expandableTableData.length > 0;

  return {
    expandableTableData,
    siblingOrdersLoading,
    siblingOrdersError: siblingOrdersError as string | null,
    hasLinkedOrders,
    selectedSiblingOrderIds,
    setSelectedSiblingOrderIds,
    isSiblingOrderSelectionDisabled,
    setIsSiblingOrderSelectionDisabled,
  };
};
